import { useMemo, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { LOCALE_NAMES, SUPPORTED_LOCALES, type Locale } from '@zt/i18n';
import {
  Badge,
  Button,
  Dialog,
  Field,
  Input,
  KeyValue,
  Section,
  Select,
  Spinner,
  Textarea,
  type Tone,
} from '@zt/ui';
import { api } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type {
  AuditEntry,
  AuditPage,
  HelpRow,
  MemberList,
  NotificationSettings,
  SystemStatus,
} from '../lib/types';
import { useMeta } from './team';
import { AlertsSection, CspReportsSection, MailDnsSection } from './system-alerts';

// ------------------------------------------------------------------ Denetim kayıtları

function JsonBlock({ value }: { value: unknown }) {
  if (value === null || value === undefined) return <span className="text-ink-3">—</span>;
  return (
    <pre className="max-h-72 overflow-auto rounded-sm bg-surface-2 p-3 text-[0.8125rem] leading-relaxed">
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

/** Panelde süzülebilen kayıt türleri (packages/core denetim kayıtlarındaki entityType değerleri). */
const AUDIT_ENTITIES = [
  'organization',
  'brand',
  'store',
  'company',
  'customer',
  'domain',
  'market',
  'membership',
  'invitation',
  'custom_role',
  'api_key',
  'webhook_endpoint',
  'integration',
  'settings',
  'setup_step',
  'help_entry',
] as const;

/** Kaydın okunabilir adı (varsa): marka/mağaza adı, alan adı, e-posta... */
function recordName(e: AuditEntry): string | null {
  for (const src of [e.after, e.before] as Array<Record<string, unknown> | null>) {
    if (!src || typeof src !== 'object') continue;
    for (const key of ['name', 'hostname', 'email', 'title', 'key']) {
      const v = src[key];
      if (typeof v === 'string' && v && v !== '[gizli]') return v;
    }
  }
  return null;
}

export function AuditPageView() {
  const t = useT();
  const { formatDate } = useI18n();
  const [entityType, setEntityType] = useState('');
  const [selected, setSelected] = useState<AuditEntry | null>(null);
  const members = useQuery({
    queryKey: ['members'],
    queryFn: () => api.get<MemberList>('/v1/admin/users'),
    retry: false,
  });
  const names = useMemo(
    () => new Map((members.data?.members ?? []).map((m) => [m.userId, m.name])),
    [members.data],
  );
  const q = useInfiniteQuery({
    queryKey: ['audit', entityType],
    initialPageParam: '',
    queryFn: ({ pageParam }) => {
      const p = new URLSearchParams({ limit: '50' });
      if (pageParam) p.set('cursor', pageParam);
      if (entityType) p.set('entityType', entityType);
      return api.get<AuditPage>(`/v1/admin/audit-logs?${p}`);
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];
  // Çevirisi olmayan yeni bir işlem kodu ham hâliyle görünür (bilgi kaybolmaz).
  const actionLabel = (code: string) => {
    const label = t(`auditActions.${code}`);
    return label === `auditActions.${code}` ? code : label;
  };
  const actorName = (e: AuditEntry) =>
    e.actorType === 'user'
      ? (names.get(e.actorId ?? '') ?? t('admin.audit.actor_user'))
      : t(`admin.audit.actor_${e.actorType}`);

  return (
    <PageFrame helpKey="page.audit">
      <div className="flex flex-wrap items-end gap-3">
        <Field label={t('admin.audit.filterEntity')} help={<HelpTip k="audit.filter" />} className="w-64">
          {(p) => (
            <Select {...p} value={entityType} onChange={(e) => setEntityType(e.target.value)}>
              <option value="">{t('admin.audit.allEntities')}</option>
              {AUDIT_ENTITIES.map((k) => (
                <option key={k} value={k}>
                  {t(`auditEntities.${k}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>
      {q.isLoading ? (
        <Spinner label={t('common.loading')} />
      ) : items.length === 0 ? (
        <p className="text-ink-2">{t('admin.audit.empty')}</p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-line bg-surface">
          <table className="w-full min-w-[40rem] text-start text-sm">
            <thead className="border-b border-line bg-surface-2 text-ink-2">
              <tr>
                <th className="px-4 py-2.5 text-start font-medium">{t('admin.audit.when')}</th>
                <th className="px-4 py-2.5 text-start font-medium">{t('admin.audit.actor')}</th>
                <th className="px-4 py-2.5 text-start font-medium">{t('admin.audit.action')}</th>
                <th className="px-4 py-2.5 text-start font-medium">{t('admin.audit.entity')}</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {items.map((e) => (
                <tr key={e.id} className="align-top">
                  <td className="tabular whitespace-nowrap px-4 py-2.5 text-ink-2">
                    {formatDate(e.createdAt)}
                  </td>
                  <td className="px-4 py-2.5">{actorName(e)}</td>
                  <td className="px-4 py-2.5">{actionLabel(e.action)}</td>
                  <td className="px-4 py-2.5 text-ink-2">
                    {t(`auditEntities.${e.entityType}`)}
                    {recordName(e) && <span className="text-ink"> {recordName(e)}</span>}
                  </td>
                  <td className="px-4 py-2.5 text-end">
                    <Button size="sm" variant="ghost" onClick={() => setSelected(e)}>
                      {t('admin.audit.details')}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {q.hasNextPage && (
        <div className="flex justify-center">
          <Button onClick={() => q.fetchNextPage()} loading={q.isFetchingNextPage}>
            {t('admin.audit.loadMore')}
          </Button>
        </div>
      )}
      {selected && (
        <Dialog
          open
          onOpenChange={() => setSelected(null)}
          variant="sheet"
          title={actionLabel(selected.action)}
          closeLabel={t('common.close')}
        >
          <div className="space-y-5">
            <KeyValue
              items={[
                { label: t('admin.audit.when'), value: formatDate(selected.createdAt) },
                { label: t('admin.audit.actor'), value: actorName(selected) },
                {
                  label: t('admin.audit.entity'),
                  value: [t(`auditEntities.${selected.entityType}`), recordName(selected)]
                    .filter(Boolean)
                    .join(' '),
                },
                { label: t('admin.audit.code'), value: <code>{selected.action}</code> },
                {
                  label: t('admin.audit.recordId'),
                  value: <code className="break-all">{selected.entityId ?? '—'}</code>,
                },
                { label: 'IP', value: selected.ip ?? '—' },
              ]}
            />
            <div>
              <p className="mb-1 font-cond font-semibold">{t('admin.audit.before')}</p>
              <JsonBlock value={selected.before} />
            </div>
            <div>
              <p className="mb-1 font-cond font-semibold">{t('admin.audit.after')}</p>
              <JsonBlock value={selected.after} />
            </div>
          </div>
        </Dialog>
      )}
    </PageFrame>
  );
}

// ------------------------------------------------------------------ Sistem durumu

const ok = (v: string): Tone => (v === 'ok' ? 'moss' : 'rose');

const ALLOWLIST_MAX = 20;

/**
 * Staging e-posta koruması (F3B-08): yalnızca staging sunucusunda görünür. Sunucudaki MAIL_ALLOWLIST
 * adresleri gösterilmez (yalnızca sayısı); işletme listesi bildirim ayarlarında tutulur.
 */
function StagingMailSection({ serverCount }: { serverCount: number }) {
  const t = useT();
  const { can } = usePermissions();
  const canRead = can('org.settings.read');
  const canEdit = can('org.settings.update');
  const q = useQuery({
    queryKey: ['settings', 'notifications'],
    queryFn: () => api.get<NotificationSettings>('/v1/admin/settings/notifications'),
    enabled: canRead,
  });
  const [draft, setDraft] = useState<string | null>(null);
  const saved = (q.data?.stagingMailAllowlist ?? []).join('\n');
  const text = draft ?? saved;
  const entries = text
    .split(/[\s,;]+/)
    .map((x) => x.trim())
    .filter(Boolean);
  const save = useApiMutation(
    () =>
      api.put<NotificationSettings>('/v1/admin/settings/notifications', {
        ...q.data,
        stagingMailAllowlist: entries,
      }),
    {
      invalidate: [['settings', 'notifications']],
      success: t('admin.system.mailGuardSaved'),
      onSuccess: () => setDraft(null),
    },
  );
  const empty = serverCount === 0 && (q.data?.stagingMailAllowlist.length ?? 0) === 0;
  return (
    <div id="staging-eposta" className="scroll-mt-4">
      <Section title={t('admin.system.mailGuardTitle')} description={t('admin.system.mailGuardLead')}>
        <div className="space-y-4">
          <p className="text-sm text-ink-2">{t('admin.system.mailGuardServer', { count: serverCount })}</p>
          {empty && q.data && (
            <p
              className="rounded-sm border border-amber/30 bg-amber-tint px-3 py-2 text-sm text-ink"
              role="alert"
            >
              {t('admin.system.mailGuardEmpty')}
            </p>
          )}
          {canRead &&
            (!q.data ? (
              <Spinner label={t('common.loading')} />
            ) : (
              <form
                className="max-w-xl space-y-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  save.mutate(undefined);
                }}
              >
                <Field
                  label={t('admin.system.mailGuardList')}
                  description={t('admin.system.mailGuardListHint')}
                  help={<HelpTip k="system.mail_allowlist" />}
                  error={
                    entries.length > ALLOWLIST_MAX
                      ? t('admin.system.mailGuardTooMany')
                      : fieldError(save.error, 'stagingMailAllowlist')
                  }
                >
                  {(p) => (
                    <Textarea
                      {...p}
                      rows={5}
                      value={text}
                      disabled={!canEdit}
                      spellCheck={false}
                      autoCapitalize="none"
                      placeholder={t('admin.system.mailGuardPlaceholder')}
                      onChange={(e) => setDraft(e.target.value)}
                    />
                  )}
                </Field>
                {canEdit && (
                  <div className="flex items-center gap-1">
                    <Button
                      type="submit"
                      variant="primary"
                      loading={save.isPending}
                      disabled={draft === null || entries.length > ALLOWLIST_MAX}
                    >
                      {t('admin.system.mailGuardSave')}
                    </Button>
                    <HelpTip k="system.mail_allowlist_save" />
                  </div>
                )}
              </form>
            ))}
        </div>
      </Section>
    </div>
  );
}

export function SystemPage() {
  const t = useT();
  const { formatDate } = useI18n();
  const meta = useMeta();
  const q = useQuery({
    queryKey: ['system-status'],
    queryFn: () => api.get<SystemStatus>('/v1/admin/system/status'),
  });
  const s = q.data;
  return (
    <PageFrame
      helpKey="page.system"
      actions={
        <div className="flex items-center gap-1">
          <Button onClick={() => q.refetch()} loading={q.isFetching}>
            {t('admin.system.refresh')}
          </Button>
          <HelpTip k="system.refresh" />
        </div>
      }
    >
      {!s ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <>
          <p className="text-sm text-ink-3">
            {t('admin.system.checkedAt', { time: formatDate(s.checkedAt) })}
          </p>
          <Section title={t('admin.system.services')}>
            <KeyValue
              items={[
                ...(meta.data
                  ? [
                      {
                        label: t('admin.system.environment'),
                        value: (
                          <Badge tone={meta.data.environment === 'production' ? 'moss' : 'amber'}>
                            {t(`admin.system.env_${meta.data.environment}`)}
                          </Badge>
                        ),
                      },
                    ]
                  : []),
                {
                  label: t('admin.system.database'),
                  value: (
                    <Badge tone={ok(s.database)}>
                      {s.database === 'ok' ? t('admin.system.ok') : t('admin.system.error')}
                    </Badge>
                  ),
                },
                {
                  label: t('admin.system.redis'),
                  value: (
                    <Badge tone={ok(s.redis)}>
                      {s.redis === 'ok' ? t('admin.system.ok') : t('admin.system.error')}
                    </Badge>
                  ),
                },
                {
                  label: t('admin.system.mail'),
                  value: (
                    <Badge tone={s.mail === 'not_configured' ? 'amber' : 'moss'}>
                      {t(`admin.system.mail_${s.mail}`)}
                    </Badge>
                  ),
                },
                ...(s.media
                  ? [
                      {
                        label: t('admin.system.media'),
                        value: (
                          <Badge tone={s.media.writable ? 'moss' : 'rose'}>
                            {t(
                              `admin.system.media_${s.media.writable ? 'ok' : (s.media.reason ?? 'storage_write_failed')}`,
                            )}
                          </Badge>
                        ),
                      },
                    ]
                  : []),
                {
                  label: t('admin.system.failedJobs'),
                  value: <span className="tabular">{s.failedJobs}</span>,
                },
                {
                  label: t('admin.system.backup'),
                  value: s.lastBackup
                    ? `${formatDate(s.lastBackup.startedAt)} (${t(`admin.system.runStatus.${s.lastBackup.status}`)})`
                    : t('admin.system.none'),
                },
                {
                  label: t('admin.system.restoreTest'),
                  value: s.lastRestoreTest
                    ? `${formatDate(s.lastRestoreTest.startedAt)} (${t(`admin.system.runStatus.${s.lastRestoreTest.status}`)})`
                    : t('admin.system.none'),
                },
                {
                  label: t('admin.system.offsite'),
                  value: !s.offsite.configured ? (
                    <span className="flex flex-wrap items-center gap-2">
                      <Badge tone="amber">{t('admin.system.offsiteSetup')}</Badge>
                      <Link to="/entegrasyonlar" className="text-pine underline underline-offset-2">
                        {t('admin.nav.integrations')}
                      </Link>
                      <HelpTip k="system.offsite" />
                    </span>
                  ) : s.offsite.last ? (
                    <span className="flex flex-wrap items-center gap-2">
                      {`${formatDate(s.offsite.last.startedAt)} (${t(`admin.system.runStatus.${s.offsite.last.status}`)})`}
                      <span className="text-ink-3">
                        {t('admin.system.offsiteKeep', { days: s.offsite.keepDays ?? 0 })}
                      </span>
                      <HelpTip k="system.offsite" />
                    </span>
                  ) : (
                    <span className="flex flex-wrap items-center gap-2">
                      {t('admin.system.offsitePending')}
                      <HelpTip k="system.offsite" />
                    </span>
                  ),
                },
              ]}
            />
          </Section>
          <AlertsSection alerts={s.alerts} />
          {meta.data?.mailGuard?.active && (
            <StagingMailSection serverCount={meta.data.mailGuard.serverAllowlistCount} />
          )}
          <Section title={t('admin.system.queues')}>
            {s.queues.length === 0 ? (
              <p className="text-ink-2">{t('admin.system.noQueues')}</p>
            ) : (
              <div className="overflow-x-auto rounded-md border border-line bg-surface">
                <table className="tabular w-full text-sm">
                  <thead className="border-b border-line bg-surface-2 text-ink-2">
                    <tr>
                      <th className="px-4 py-2 text-start font-medium" />
                      <th className="px-4 py-2 text-end font-medium">{t('admin.system.waiting')}</th>
                      <th className="px-4 py-2 text-end font-medium">{t('admin.system.activeJobs')}</th>
                      <th className="px-4 py-2 text-end font-medium">{t('admin.system.delayed')}</th>
                      <th className="px-4 py-2 text-end font-medium">{t('admin.system.failed')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {s.queues.map((qq) => (
                      <tr key={qq.name} className="border-t border-line">
                        <td className="px-4 py-2">{t(`admin.system.queueName_${qq.name}`)}</td>
                        <td className="px-4 py-2 text-end">{qq.waiting}</td>
                        <td className="px-4 py-2 text-end">{qq.active}</td>
                        <td className="px-4 py-2 text-end">{qq.delayed}</td>
                        <td className={`px-4 py-2 text-end ${qq.failed ? 'font-semibold text-rose' : ''}`}>
                          {qq.failed}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
          <Section title={t('admin.system.domains')}>
            {s.domains.length === 0 ? (
              <p className="text-ink-2">{t('admin.stores.noDomains')}</p>
            ) : (
              <ul className="divide-y divide-line rounded-md border border-line bg-surface">
                {s.domains.map((d) => (
                  <li key={d.hostname} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3">
                    <span className="flex-1 font-medium break-all">{d.hostname}</span>
                    <Badge
                      tone={
                        d.tlsStatus === 'ok'
                          ? 'moss'
                          : d.tlsStatus === 'unknown'
                            ? 'slate'
                            : d.tlsStatus === 'expiring'
                              ? 'amber'
                              : 'rose'
                      }
                    >
                      {d.tlsStatus}
                    </Badge>
                    <span className="text-sm text-ink-3">
                      {d.daysLeft !== null
                        ? t('admin.system.daysLeft', { days: d.daysLeft })
                        : t('common.unknown')}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Section>
          <CspReportsSection />
          <MailDnsSection />
        </>
      )}
    </PageFrame>
  );
}

// ------------------------------------------------------------------ Yardım metinleri editörü

function HelpEditDialog({ row, onClose }: { row: HelpRow; onClose: () => void }) {
  const t = useT();
  const [f, setF] = useState({
    title: row.title,
    short: row.short,
    whatItDoes: row.whatItDoes ?? '',
    whenToUse: row.whenToUse ?? '',
    dataChanged: row.dataChanged ?? '',
    requiredPermission: row.requiredPermission ?? '',
    reversible: row.reversible ?? '',
  });
  const save = useApiMutation(
    () =>
      api.put(`/v1/admin/help/${encodeURIComponent(row.key)}/${row.locale}`, {
        ...Object.fromEntries(
          Object.entries(f).map(([k, v]) => [
            k,
            v.trim() === '' && !['title', 'short'].includes(k) ? null : v,
          ]),
        ),
        commonErrors: row.commonErrors,
        guideUrl: row.guideUrl,
      }),
    { invalidate: [['help'], ['help-editor']], success: t('admin.helpEditor.saved'), onSuccess: onClose },
  );
  const history = useQuery({
    queryKey: ['help-history', row.key, row.locale],
    queryFn: () =>
      api.get<Array<{ id: string; version: number; createdAt: string }>>(
        `/v1/admin/help/${encodeURIComponent(row.key)}/${row.locale}/history`,
      ),
  });
  const { formatDate } = useI18n();
  const field = (k: keyof typeof f, label: string, long = true) => (
    <Field label={label}>
      {(p) =>
        long ? (
          <Textarea {...p} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} />
        ) : (
          <Input {...p} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} />
        )
      }
    </Field>
  );
  return (
    <Dialog
      open
      onOpenChange={onClose}
      variant="sheet"
      title={row.key}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate(undefined)}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {field('title', t('admin.helpEditor.title'), false)}
        {field('short', t('admin.helpEditor.short'))}
        {field('whatItDoes', t('admin.help.whatItDoes'))}
        {field('whenToUse', t('admin.help.whenToUse'))}
        {field('dataChanged', t('admin.help.dataChanged'))}
        {field('requiredPermission', t('admin.help.requiredPermission'))}
        {field('reversible', t('admin.help.reversible'))}
        <div>
          <p className="mb-1 font-cond font-semibold">{t('admin.helpEditor.history')}</p>
          {!history.data?.length ? (
            <p className="text-sm text-ink-3">{t('admin.helpEditor.noHistory')}</p>
          ) : (
            <ul className="text-sm text-ink-2">
              {history.data.map((h) => (
                <li key={h.id}>
                  v{h.version} — {formatDate(h.createdAt)}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Dialog>
  );
}

export function HelpEditorPage() {
  const t = useT();
  const { locale } = useI18n();
  const [loc, setLoc] = useState<Locale>(locale);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<HelpRow | null>(null);
  const q = useQuery({
    queryKey: ['help-editor', loc],
    queryFn: () =>
      // Panel ve mobil uygulamanın tüm yardım kayıtları (F10-06).
      api.get<HelpRow[]>(`/v1/admin/help?scope=all&locale=${loc}`),
  });
  const rows = (q.data ?? [])
    .filter((r) => r.locale === loc || loc === 'tr')
    .filter(
      (r) =>
        !search ||
        r.key.includes(search) ||
        r.title.toLocaleLowerCase(loc).includes(search.toLocaleLowerCase(loc)),
    )
    .sort((a, b) => a.key.localeCompare(b.key));
  return (
    <PageFrame helpKey="page.help_editor" actions={<HelpTip k="help.edit" />}>
      <div className="grid gap-3 sm:grid-cols-[1fr_12rem]">
        <Field label={t('admin.helpEditor.search')}>
          {(p) => <Input {...p} value={search} onChange={(e) => setSearch(e.target.value)} />}
        </Field>
        <Field label={t('admin.helpEditor.locale')}>
          {(p) => (
            <Select {...p} value={loc} onChange={(e) => setLoc(e.target.value as Locale)}>
              {SUPPORTED_LOCALES.map((l) => (
                <option key={l} value={l}>
                  {LOCALE_NAMES[l]}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>
      {!q.data ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <ul className="divide-y divide-line rounded-md border border-line bg-surface">
          {rows.map((r) => (
            <li
              key={`${r.key}:${r.locale}`}
              className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5"
            >
              <div className="min-w-0 flex-1">
                <p className="font-medium">{r.title}</p>
                <code className="text-[0.75rem] text-ink-3">{r.key}</code>
              </div>
              {r.customized && <Badge tone="amber">{t('admin.helpEditor.customized')}</Badge>}
              <span className="tabular text-sm text-ink-3">v{r.version}</span>
              <Button size="sm" onClick={() => setEditing(r)}>
                {t('admin.helpEditor.edit')}
              </Button>
            </li>
          ))}
        </ul>
      )}
      {editing && <HelpEditDialog row={editing} onClose={() => setEditing(null)} />}
    </PageFrame>
  );
}
